/**
 * Slice extraction + surface shell for Voxel Quest models.
 */

import { cluesFromGrid, isUniquelyLineSolvable } from './dpSolver.js';

/** Extract 2D slice: axis x|y|z at index. Returns binary grid [u][v]. */
export function extractSlice(volume, axis, index) {
    const s = volume.length;
    const grid = Array.from({ length: s }, () => Array(s).fill(0));
    for (let a = 0; a < s; a++) {
        for (let b = 0; b < s; b++) {
            let v = 0;
            if (axis === 'z') v = volume[index][a][b]; // y,x
            else if (axis === 'y') v = volume[a][index][b]; // z,x
            else if (axis === 'x') v = volume[a][b][index]; // z,y
            grid[a][b] = v ? 1 : 0;
        }
    }
    return grid;
}

/** Surface shell voxels: only those with an empty orthogonal neighbor (or edge). */
export function extractSurfaceShell(volume) {
    const s = volume.length;
    const shell = [];
    const dirs = [
        [1, 0, 0],
        [-1, 0, 0],
        [0, 1, 0],
        [0, -1, 0],
        [0, 0, 1],
        [0, 0, -1],
    ];
    for (let z = 0; z < s; z++) {
        for (let y = 0; y < s; y++) {
            for (let x = 0; x < s; x++) {
                const mat = volume[z][y][x];
                if (!mat) continue;
                let exposed = false;
                for (const [dx, dy, dz] of dirs) {
                    const nx = x + dx;
                    const ny = y + dy;
                    const nz = z + dz;
                    if (nx < 0 || ny < 0 || nz < 0 || nx >= s || ny >= s || nz >= s) {
                        exposed = true;
                        break;
                    }
                    if (!volume[nz][ny][nx]) {
                        exposed = true;
                        break;
                    }
                }
                if (exposed) shell.push({ x, y, z, mat });
            }
        }
    }
    return shell;
}

/** Voxels contributed when slice (axis,index) is solved — intersection with surface shell. */
export function shellVoxelsForSlice(volume, axis, index) {
    const shell = extractSurfaceShell(volume);
    return shell.filter((v) => {
        if (axis === 'z') return v.z === index;
        if (axis === 'y') return v.y === index;
        if (axis === 'x') return v.x === index;
        return false;
    });
}

/**
 * Jaccard similarity on filled cells (0 = disjoint silhouettes, 1 = identical).
 * Used to keep consecutive suggestions from feeling like the same puzzle.
 */
export function sliceGridJaccard(a, b) {
    if (!a?.length || !b?.length || a.length !== b.length) return 0;
    let inter = 0;
    let uni = 0;
    for (let r = 0; r < a.length; r++) {
        if (!a[r] || !b[r] || a[r].length !== b[r].length) return 0;
        for (let c = 0; c < a[r].length; c++) {
            const A = !!a[r][c];
            const B = !!b[r][c];
            if (A || B) uni += 1;
            if (A && B) inter += 1;
        }
    }
    return uni ? inter / uni : 1;
}

/** Size-aware diversity knobs — Workshop 8³ tighter stacks, Atelier 16³ wider gaps. */
export function sliceDiversityRules(size) {
    if (size <= 8) {
        return {
            minIndexGap: 2,
            maxJaccard: 0.72,
            sameAxisPenalty: 18,
            // Must beat ~100–200 spoiler stage gaps so next claim prefers another axis.
            lastSameAxisPenalty: 160,
            nearIndexPenalty: 45,
            highSimPenalty: 55,
        };
    }
    return {
        minIndexGap: 3,
        maxJaccard: 0.75,
        sameAxisPenalty: 16,
        lastSameAxisPenalty: 220,
        nearIndexPenalty: 40,
        highSimPenalty: 50,
    };
}

function sliceMeta(item) {
    return {
        axis: item?.data?.axis ?? item?.axis ?? null,
        index: item?.data?.index ?? item?.index ?? null,
        fillRate: item?.data?.fillRate ?? item?.fillRate ?? 0.5,
        grid: item?.data?.solutionGrid ?? item?.solutionGrid ?? null,
    };
}

/**
 * Lower score = safer early reveal (less of a recognizable silhouette).
 * Order intent: top → bottom → back → front → sides → mid-body.
 * Our Y grows down in slice space (small y = top of model).
 */
export function sliceSpoilerScore(axis, index, size, fillRate = 0.5) {
    if (axis == null || index == null || !size) return 999;
    const t = index / Math.max(size - 1, 1);
    const edgeDist = Math.min(index, size - 1 - index); // 0 at rim
    const midness = 1 - edgeDist / (size / 2); // 1 at center

    let stage = 20;
    if (axis === 'y' && t <= 0.34) stage = 0; // top
    else if (axis === 'y' && t >= 0.66) stage = 1; // bottom
    else if (axis === 'z' && t >= 0.66) stage = 2; // back
    else if (axis === 'z' && t <= 0.34) stage = 3; // front
    else if (axis === 'x' && (t <= 0.34 || t >= 0.66)) stage = 4; // sides
    else stage = 12; // mid-body (most spoiler-y)

    // Sparse caps/feet before dense torso cuts
    const fillPenalty = fillRate * 8;
    return stage * 100 + midness * 15 + fillPenalty + edgeDist * 0.1;
}

/**
 * Extra cost so the next default claim isn't a near-copy of recent planes.
 * @param {object} item candidate tile / slice
 * @param {object[]} avoid recently solved / claimed / already picked slices
 */
export function sliceDiversityPenalty(item, avoid, size) {
    if (!avoid?.length) return 0;
    const rules = sliceDiversityRules(size);
    const cur = sliceMeta(item);
    let penalty = 0;
    const last = sliceMeta(avoid[avoid.length - 1]);
    // Strong nudge to change axis after the plane you just finished.
    if (last.axis != null && cur.axis === last.axis) {
        penalty += rules.lastSameAxisPenalty;
    }
    for (const ref of avoid) {
        const prev = sliceMeta(ref);
        if (cur.axis == null || prev.axis == null) continue;
        if (cur.axis === prev.axis) {
            penalty += rules.sameAxisPenalty;
            if (
                cur.index != null &&
                prev.index != null &&
                Math.abs(cur.index - prev.index) < rules.minIndexGap
            ) {
                penalty += rules.nearIndexPenalty;
            }
        }
        if (cur.grid && prev.grid) {
            const j = sliceGridJaccard(cur.grid, prev.grid);
            if (j >= rules.maxJaccard) {
                penalty += rules.highSimPenalty + j * 25;
            } else if (j >= rules.maxJaccard - 0.12) {
                penalty += rules.highSimPenalty * 0.45;
            }
        }
    }
    return penalty;
}

/**
 * Rank open tiles / slice specs for default suggestion.
 * Least spoiler first, then diversity vs `avoid` (solved / last played).
 */
export function rankSlicesForSuggestion(items, size, opts = {}) {
    const avoid = opts.avoid || [];
    return [...items].sort((a, b) => {
        const am = sliceMeta(a);
        const bm = sliceMeta(b);
        const as =
            sliceSpoilerScore(am.axis, am.index, size, am.fillRate) +
            sliceDiversityPenalty(a, avoid, size);
        const bs =
            sliceSpoilerScore(bm.axis, bm.index, size, bm.fillRate) +
            sliceDiversityPenalty(b, avoid, size);
        if (as !== bs) return as - bs;
        // Stable tie-break: axis then index
        if (am.axis !== bm.axis) return String(am.axis).localeCompare(String(bm.axis));
        return (am.index ?? 0) - (bm.index ?? 0);
    });
}

function conflictsWithPicked(candidate, picked, size, mode) {
    const rules = sliceDiversityRules(size);
    const minGap = mode === 'strict' ? rules.minIndexGap : Math.max(1, rules.minIndexGap - 1);
    const maxJac = mode === 'strict' ? rules.maxJaccard : Math.min(0.9, rules.maxJaccard + 0.1);

    for (const p of picked) {
        if (p.axis === candidate.axis && p.index === candidate.index) return true;
        const j = sliceGridJaccard(p.solutionGrid, candidate.solutionGrid);
        // Always drop near-identical silhouettes (e.g. duplicate heart lobes).
        if (j >= 0.95) return true;
        if (mode === 'loose') continue;
        if (p.axis === candidate.axis && Math.abs(p.index - candidate.index) < minGap) {
            return true;
        }
        if (j >= maxJac) return true;
    }
    return false;
}

/**
 * Greedy order: each next slice diversifies vs already-ordered ones.
 * Keeps spoiler preference without clustering Y=n, Y=n+1, Y=n+2.
 */
export function orderSlicesWithDiversity(items, size) {
    const remaining = [...items];
    const ordered = [];
    while (remaining.length) {
        const [next] = rankSlicesForSuggestion(remaining, size, { avoid: ordered });
        if (!next) break;
        ordered.push(next);
        const idx = remaining.findIndex((r) => {
            const a = sliceMeta(r);
            const b = sliceMeta(next);
            return a.axis === b.axis && a.index === b.index;
        });
        if (idx >= 0) remaining.splice(idx, 1);
        else remaining.shift();
    }
    return ordered;
}

/**
 * Build playable slice catalog for a model.
 * Prefers uniquely solvable planes, ordered for gradual reveal
 * (extremes / sparse first — not mid-body silhouettes), with spacing so
 * consecutive suggestions aren't near-duplicate silhouettes.
 */
export function buildValidatedSlices(model, maxSlices = 16) {
    const { volume, size } = model;
    const candidates = [];
    for (const axis of ['z', 'y', 'x']) {
        for (let i = 0; i < size; i++) {
            const grid = extractSlice(volume, axis, i);
            const fill = grid.flat().reduce((a, b) => a + b, 0) / (size * size);
            if (fill < 0.05 || fill > 0.94) continue;
            const unique = isUniquelyLineSolvable(grid);
            candidates.push({
                axis,
                index: i,
                clues: cluesFromGrid(grid),
                solutionGrid: grid,
                fillRate: fill,
                unique,
                spoilerScore: sliceSpoilerScore(axis, i, size, fill),
            });
        }
    }

    candidates.sort((a, b) => {
        if (a.unique !== b.unique) return a.unique ? -1 : 1;
        return a.spoilerScore - b.spoilerScore;
    });

    const picked = [];
    const axisCount = { x: 0, y: 0, z: 0 };
    const maxPerAxis = Math.max(3, Math.ceil(maxSlices / 2));

    const take = (allowSoft, diversityMode) => {
        for (const c of candidates) {
            if (picked.length >= maxSlices) break;
            if (!c.unique && !allowSoft) continue;
            if (axisCount[c.axis] >= maxPerAxis) continue;
            if (conflictsWithPicked(c, picked, size, diversityMode)) continue;
            picked.push({
                axis: c.axis,
                index: c.index,
                clues: c.clues,
                solutionGrid: c.solutionGrid,
                fillRate: c.fillRate,
                softValidated: !c.unique,
                spoilerScore: c.spoilerScore,
            });
            axisCount[c.axis] += 1;
        }
    };

    // Strict diversity first, then relax so we still fill the vault.
    take(false, 'strict');
    take(false, 'relaxed');
    take(true, 'strict');
    take(true, 'relaxed');
    // Last resort: fill count, but still reject near-identical grids (see conflictsWithPicked).
    if (picked.length < Math.ceil(maxSlices * 0.65)) {
        take(true, 'loose');
    }

    // Suggestion order diversifies as the player works through the vault.
    return orderSlicesWithDiversity(picked, size);
}

/** Public hologram payload: surface positions only (no name).
 * True palette hex is attached only when the vault is fully revealed
 * (`revealedKeys === null`) so in-progress shells stay clay until ceremony.
 */
export function publicShellPayload(model, revealedKeys = null) {
    const shell = extractSurfaceShell(model.volume);
    const pal = model.palette || {};
    const fullyRevealed = revealedKeys === null;
    return {
        size: model.size,
        category: model.category,
        voxels: shell.map((v) => {
            const key = `${v.x},${v.y},${v.z}`;
            const revealed = fullyRevealed || revealedKeys.has(key);
            const color = pal[v.mat] || pal[String(v.mat)] || '#dce6f5';
            return {
                x: v.x,
                y: v.y,
                z: v.z,
                mat: fullyRevealed ? v.mat : 0,
                color: fullyRevealed ? color : undefined,
                revealed,
            };
        }),
        palette: fullyRevealed ? pal : {},
        revealed: fullyRevealed,
    };
}
